import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Drawer,
  Skeleton,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import type { IamPolicyPermissionAction } from '@/api/iam.api';
import DsIcon from '@/components/Layout/DsIcon';
import { PolicyPermissionView } from '@/pages/iam/components/PolicyPermissionView';
import {
  usePolicyPermissionItems,
  type IamPolicyStatement,
  type PermissionAction,
} from '@/pages/iam/composables/usePolicyPermissionItems';
import { fmtTime } from '@/utils/utils';
import './PolicySelector.css';

const EMPTY_ACTION_CATALOG: PermissionAction[] = [];

type IamPolicyType = 'SYSTEM' | 'CUSTOM';

export interface PolicySelectionItem {
  id: string;
  name: string;
  description?: string;
  type: IamPolicyType;
  statementCount: number;
  permissionCount: number;
}

export interface PolicyPreviewData {
  id: string;
  name: string;
  description?: string;
  type: IamPolicyType;
  statements: IamPolicyStatement[];
  authorizationCount: number;
  createdAt: string;
  permissionActions?: IamPolicyPermissionAction[];
  source?: unknown;
}

function toLegacyPolicySource(policy: PolicyPreviewData) {
  return {
    version: '1',
    statement: policy.statements.map((statement) => ({
      effect: statement.effect,
      action: statement.actions,
      ...(statement.resourceScope === 'OWN'
        ? {
            condition: {
              string_equals: {
                'inf:resourceOwner': '{self}',
              },
            },
          }
        : {}),
    })),
  };
}

interface PolicySummaryCardProps {
  policy: PolicySelectionItem;
  selected?: boolean;
  selectable?: boolean;
  disabled?: boolean;
  statusLabel?: string;
  onPreview: (policy: PolicySelectionItem) => void;
}

export function PolicySummaryCard({
  policy,
  selected = false,
  selectable = false,
  disabled = false,
  statusLabel,
  onPreview,
}: PolicySummaryCardProps) {
  const copy = (
    <span className="iam-policy-selector-card-copy">
      <span className="iam-policy-selector-card-title">
        <Typography.Text strong>{policy.name}</Typography.Text>
        <Tag bordered={false} color={policy.type === 'SYSTEM' ? 'blue' : 'purple'}>
          {policy.type === 'SYSTEM' ? '系统策略' : '自定义策略'}
        </Tag>
        {statusLabel && <Tag bordered={false}>{statusLabel}</Tag>}
      </span>
      <Typography.Text type="secondary" className="iam-policy-selector-card-description">
        {policy.description || '暂无策略描述'}
      </Typography.Text>
      <span className="iam-policy-selector-card-meta">
        <Typography.Text type="secondary">
          {policy.statementCount} 条规则 · {policy.permissionCount} 个权限点
        </Typography.Text>
      </span>
    </span>
  );

  return (
    <div
      className={`iam-policy-selector-card${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}
    >
      {selectable ? (
        <Checkbox className="iam-policy-selector-card-choice" value={policy.id} disabled={disabled}>
          {copy}
        </Checkbox>
      ) : (
        <div className="iam-policy-selector-card-readonly">{copy}</div>
      )}
      <Button
        type="link"
        size="small"
        className="iam-policy-selector-card-detail"
        onClick={() => onPreview(policy)}
      >
        查看详情
      </Button>
    </div>
  );
}

interface PolicyCardSelectorProps {
  policies: PolicySelectionItem[];
  value?: string[];
  onChange?: (value: string[]) => void;
  onPreview: (policy: PolicySelectionItem) => void;
  disabled?: boolean;
  disabledPolicyIds?: string[];
  loading?: boolean;
}

export function PolicyCardSelector({
  policies,
  value = [],
  onChange,
  onPreview,
  disabled = false,
  disabledPolicyIds = [],
  loading = false,
}: PolicyCardSelectorProps) {
  const selectablePolicyIds = policies
    .filter((policy) => !disabled && !disabledPolicyIds.includes(policy.id))
    .map((policy) => policy.id);
  const allSelected =
    selectablePolicyIds.length > 0 &&
    selectablePolicyIds.every((policyId) => value.includes(policyId));

  const toggleAll = () => {
    if (allSelected) {
      onChange?.(value.filter((policyId) => !selectablePolicyIds.includes(policyId)));
      return;
    }
    onChange?.([...new Set([...value, ...selectablePolicyIds])]);
  };

  return (
    <Spin spinning={loading}>
      <div className="iam-policy-selector">
        <div className="iam-policy-selector-toolbar">
          <Typography.Text type="secondary">
            已选择 {value.length} / {policies.length} 个策略
          </Typography.Text>
          {selectablePolicyIds.length > 0 && (
            <Button type="link" size="small" onClick={toggleAll}>
              {allSelected ? '取消全选' : '全选'}
            </Button>
          )}
        </div>
        <Checkbox.Group
          className="iam-policy-selector-card-grid"
          value={value}
          onChange={(next) => onChange?.(next as string[])}
        >
          {policies.map((policy) => {
            const alreadyGranted = disabledPolicyIds.includes(policy.id);
            return (
              <PolicySummaryCard
                key={policy.id}
                policy={policy}
                selected={value.includes(policy.id)}
                selectable
                disabled={disabled || alreadyGranted}
                statusLabel={alreadyGranted ? '已授权' : undefined}
                onPreview={onPreview}
              />
            );
          })}
        </Checkbox.Group>
      </div>
    </Spin>
  );
}

interface PolicyPreviewDrawerProps {
  open: boolean;
  policy?: PolicyPreviewData;
  actions?: PermissionAction[];
  loading?: boolean;
  error?: string;
  onClose: () => void;
  afterClose: () => void;
  onViewDetail?: (policy: PolicyPreviewData) => void;
}

export function PolicyPreviewDrawer({
  open,
  policy,
  actions = EMPTY_ACTION_CATALOG,
  loading = false,
  error,
  onClose,
  afterClose,
  onViewDetail,
}: PolicyPreviewDrawerProps) {
  const [policySourceView, setPolicySourceView] = useState(false);
  const permissionItems = usePolicyPermissionItems({
    actions: policy?.permissionActions,
    statements: policy?.statements,
    actionCatalog: actions,
  });

  return (
    <Drawer
      open={open}
      width={960}
      title={
        policy ? (
          <Space size={8} wrap>
            <Typography.Text strong>{policy.name}</Typography.Text>
            <Tag bordered={false} color={policy.type === 'SYSTEM' ? 'blue' : 'purple'}>
              {policy.type === 'SYSTEM' ? '系统策略' : '自定义策略'}
            </Tag>
          </Space>
        ) : (
          '策略详情'
        )
      }
      onClose={onClose}
      afterOpenChange={(nextOpen) => {
        if (!nextOpen) afterClose();
      }}
      footer={
        <div className="iam-policy-selector-drawer-footer">
          <Button onClick={onClose}>关闭</Button>
          {onViewDetail && (
            <Button
              type="primary"
              icon={<DsIcon name="external-link" size={14} />}
              iconPosition="start"
              disabled={!policy}
              onClick={() => policy && onViewDetail(policy)}
            >
              查看策略详情
            </Button>
          )}
        </div>
      }
      destroyOnHidden
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : error ? (
        <Alert type="error" showIcon message="策略详情加载失败" description={error} />
      ) : policy ? (
        <div className="iam-policy-selector-content">
          <Descriptions
            bordered
            column={1}
            items={[
              { key: 'name', label: '策略名', children: policy.name },
              { key: 'desc', label: '描述', children: policy.description || '—' },
              { key: 'count', label: '授权次数', children: `${policy.authorizationCount} 次` },
              { key: 'createdAt', label: '创建时间', children: fmtTime(policy.createdAt) },
            ]}
          />
          <div className="iam-policy-selector-content-head">
            <Typography.Title level={5}>策略内容</Typography.Title>
            <Button onClick={() => setPolicySourceView((value) => !value)}>
              {policySourceView ? '返回列表' : '源码查看'}
            </Button>
          </div>
          {policySourceView ? (
            <pre className="iam-policy-selector-source">
              {JSON.stringify(policy.source ?? toLegacyPolicySource(policy), null, 2)}
            </pre>
          ) : (
            <PolicyPermissionView items={permissionItems} />
          )}
        </div>
      ) : null}
    </Drawer>
  );
}
